import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Empty, Input, Modal, Segmented, Select, Spin, Tag } from 'antd';

import {
  fetchArticleBankEntries,
  fetchArticleBankFacets,
} from '../../service/articleBank';

// "Add article" window for the PDF Page Builder: search the Articles Bank,
// narrow by body system / toxic exposure / part of the claim, and add the
// articles an appeal needs. The window stays open so several can be added.

const PAGE_SIZE = 20;

const PART_OPTIONS = [
  { label: 'Any part', value: 'any' },
  { label: 'Nexus', value: 'nexus' },
  { label: 'Evaluation', value: 'evaluation' },
  { label: 'Both', value: 'both' },
];

const dataOf = (response) => response?.data ?? response ?? {};

const facetOptions = (facets) =>
  (facets || []).map((facet) => ({
    value: facet.label,
    label: `${facet.label} (${facet.count})`,
  }));

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  > * {
    flex: 1 1 200px;
    min-width: 0;
  }
`;

const Results = styled.div`
  max-height: 52vh;
  overflow-y: auto;
  border: 1px solid #e3e8ef;
  border-radius: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef1f5;

  &:last-child {
    border-bottom: none;
  }
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.35;
`;

const RowMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const RowTags = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  .ant-tag {
    margin: 0;
  }
`;

const RowAction = styled.div`
  flex: none;
  font-size: 12px;
  color: #15803d;
  font-weight: 600;
  padding-top: 2px;
`;

const Note = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
`;

const MoreRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px;
`;

const Count = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 6px;
`;

const ArticlePickerModal = ({ open, onClose, onAdd, addedArticleIds }) => {
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState('');
  const [bodySystem, setBodySystem] = useState(undefined);
  const [exposure, setExposure] = useState(undefined);
  const [claimPart, setClaimPart] = useState('any');
  const [facets, setFacets] = useState({ body_systems: [], exposures: [] });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searched, setSearched] = useState(false);
  const facetsLoadedRef = useRef(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open || facetsLoadedRef.current) return;
    facetsLoadedRef.current = true;
    fetchArticleBankFacets()
      .then((response) => setFacets(dataOf(response)))
      .catch(() => {
        // Filters are a convenience; search still works without them.
        facetsLoadedRef.current = false;
      });
  }, [open]);

  const load = useCallback(
    async (nextPage) => {
      const token = requestRef.current + 1;
      requestRef.current = token;
      setLoading(true);
      setError(false);
      try {
        const response = await fetchArticleBankEntries({
          page: nextPage,
          pageSize: PAGE_SIZE,
          query,
          bodySystems: bodySystem ? [bodySystem] : [],
          exposures: exposure ? [exposure] : [],
          claimParts: claimPart === 'any' ? [] : [claimPart],
        });
        if (requestRef.current !== token) return;
        const data = dataOf(response);
        setResults((prev) =>
          nextPage === 1 ? data.results || [] : [...prev, ...(data.results || [])]
        );
        setTotal(data.count || 0);
        setPage(nextPage);
      } catch {
        if (requestRef.current !== token) return;
        setError(true);
      } finally {
        if (requestRef.current === token) {
          setLoading(false);
          setSearched(true);
        }
      }
    },
    [query, bodySystem, exposure, claimPart]
  );

  // First page whenever the window opens or a search/filter changes.
  useEffect(() => {
    if (open) load(1);
  }, [open, load]);

  const addedIds = addedArticleIds || new Set();
  const hasMore = results.length < total;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Add an article to the builder"
      width={720}
      footer={
        <Button type="primary" onClick={onClose} data-testid="article-picker-done">
          Done
        </Button>
      }
    >
      <Filters>
        <Input.Search
          allowClear
          placeholder="Search articles, e.g. burn pits asthma"
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            if (!event.target.value) setQuery('');
          }}
          onSearch={(value) => setQuery(value.trim())}
          data-testid="article-picker-search"
        />
        <FilterRow>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Body system"
            value={bodySystem}
            onChange={setBodySystem}
            options={facetOptions(facets.body_systems)}
            data-testid="article-picker-body-system"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Toxic exposure"
            value={exposure}
            onChange={setExposure}
            options={facetOptions(facets.exposures)}
            data-testid="article-picker-exposure"
          />
        </FilterRow>
        <Segmented
          block
          options={PART_OPTIONS}
          value={claimPart}
          onChange={setClaimPart}
          data-testid="article-picker-part"
        />
      </Filters>

      {error ? (
        <Note>
          Could not load articles.{' '}
          <Button type="link" size="small" onClick={() => load(1)}>
            Try again
          </Button>
        </Note>
      ) : (
        <>
          {!loading && total > 0 && (
            <Count>
              {total} article{total === 1 ? '' : 's'}
            </Count>
          )}
          <Results data-testid="article-picker-results">
            {results.map((article) => {
              const added = addedIds.has(article.id);
              const meta = [article.authors, article.publication, article.year]
                .filter(Boolean)
                .join(' · ');
              return (
                <Row key={article.id} data-testid={`article-row-${article.id}`}>
                  <RowText>
                    <RowTitle>{article.title}</RowTitle>
                    {meta && <RowMeta>{meta}</RowMeta>}
                    <RowTags>
                      {article.claim_part_label && (
                        <Tag color="blue">{article.claim_part_label}</Tag>
                      )}
                      {(article.exposures || []).slice(0, 2).map((label) => (
                        <Tag key={`x-${label}`} color="orange">
                          {label}
                        </Tag>
                      ))}
                      {(article.body_systems || []).slice(0, 2).map((label) => (
                        <Tag key={`b-${label}`}>{label}</Tag>
                      ))}
                    </RowTags>
                  </RowText>
                  <RowAction>
                    {added ? (
                      'Added'
                    ) : article.can_add_to_packet ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => onAdd(article)}
                        data-testid={`article-add-${article.id}`}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        disabled
                        title="Saved web pages and images cannot be added to a packet yet"
                        data-testid={`article-add-${article.id}`}
                      >
                        Not a PDF
                      </Button>
                    )}
                  </RowAction>
                </Row>
              );
            })}
            {loading && (
              <Note>
                <Spin size="small" /> Loading…
              </Note>
            )}
            {searched && !loading && results.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No articles match."
              />
            )}
            {!loading && hasMore && (
              <MoreRow>
                <Button
                  onClick={() => load(page + 1)}
                  data-testid="article-picker-more"
                >
                  Show more
                </Button>
              </MoreRow>
            )}
          </Results>
        </>
      )}
    </Modal>
  );
};

ArticlePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  addedArticleIds: PropTypes.instanceOf(Set),
};

export default ArticlePickerModal;
